import { computed, inject, Injectable, Signal } from '@angular/core';
// eslint-disable-next-line no-restricted-imports -- This adapter translates feature flags into capability access results.
import { FeatureFlagsService } from '@libs/shared/common';
import { CapabilityAccessChecker, CapabilityName } from '../capability.model';

// Maps capability names to their driving feature flag signals. The composite `account` and
// `shop` capabilities are derived here from public-app and DCP app-config flags, respectively.
@Injectable({ providedIn: 'root' })
export class FeatureFlagAccessCheckerService extends CapabilityAccessChecker {
  protected readonly featureFlags: FeatureFlagsService = inject(FeatureFlagsService);

  protected readonly capabilities: Partial<Record<CapabilityName, Signal<boolean>>> = {
    // Platform
    'platform.forceNativeAppUpdate': this.featureFlags.forceNativeAppUpdateEnabled,
    'platform.cmsAppConfigMode': this.featureFlags.newAppConfigEnabled,
    'platform.publicApp': this.featureFlags.publicAppEnabled,

    // Account
    account: computed(() => !this.featureFlags.publicAppEnabled()),
    'account.employeeAdministration': this.featureFlags.employeeAdminEnabled,
    'account.customerBlockingCheck': this.featureFlags.blockingCustomersEnabled,

    // Services
    'services.srm': this.featureFlags.srmEnabled,
    'services.srmTicketSuggestion': this.featureFlags.ticketSuggestionEnabled,
    'services.orderHistoryDeliveryFilter': this.featureFlags.orderHistoryDeliveryFilterEnabled,
    'services.documents': this.featureFlags.documentsEnabled,

    // Shop
    shop: computed(() => !this.featureFlags.newAppConfigEnabled()),
    'shop.referenceText': this.featureFlags.referenceTextEnabled,
    'shop.requestedDeliveryDate': this.featureFlags.requestedDeliveryDateEnabled,
    'shop.newCheckoutUi': this.featureFlags.newCheckoutUiEnabled,
    'shop.orderVouchers': this.featureFlags.orderVouchersEnabled,
    'shop.shoppingListDownload': this.featureFlags.shoppingListDownloadEnabled,
    'shop.orderReorder': computed(() => !this.featureFlags.newAppConfigEnabled(),),
    'shop.orderExtendedDeposit': this.featureFlags.orderExtendedDepositEnabled,
  };

  public canUse(name: CapabilityName): Signal<boolean | undefined> {
    return this.capabilities[name] ?? this.neutralResult;
  }
}
